import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Box,
  InputBase,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Divider,
  IconButton,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Badge,
  Stack,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import {
  Search,
  History,
  Tune,
  EventAvailableOutlined,
  ChatBubbleOutline,
  AssignmentOutlined,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { useClients } from "../hooks/useClients";
import { useConversations } from "../hooks/useConversations";
import { useUsers } from "../hooks/useUsers";
import { useTeamSpaces } from "../hooks/useTeamSpaces";
import {
  useUserAgendaCalendars,
  useUserAgendaMutations,
} from "../hooks/useUserAgenda";
import debounce from "lodash.debounce";
import AdvancedSearchModal from "./AdvancedSearchModal";
import AgendaEventDialog from "../pages/TeamAgenda/AgendaEventDialog";
import CreateLinkedTaskDialog from "../pages/TeamTasks/components/CreateLinkedTaskDialog";
import { createTeamBoardCard, fetchData, fetchTeamBoardCardLocation } from "../api/client";
import { openTeamTasksPopupWindow } from "../utils/openTeamTasksPopupWindow";
import {
  formatTaskDisplayName,
  parseTaskIdSearchQuery,
} from "../pages/TeamTasks/utils/taskDisplayName";
import {
  EMPTY_SEARCH_FILTER_STATE,
  filterStateToModalInitial,
  filterStateToUrlString,
  modalPayloadToFilterState,
  parseSearchParamsFromUrl,
  isProtocolSearchQuery,
  normalizeProtocolSearchQuery,
} from "../utils/searchFilterState";
import { useOnboarding } from "../context/OnboardingContext";
import {
  getSelectedCompanyIds,
  readWorkspaceSessionJson,
  writeWorkspaceSessionJson,
  readWorkspaceLocalJson,
  writeWorkspaceLocalJson,
  WS_KEY,
} from "../utils/workspaceStorage";
import {
  getClientProfilePictureUrl,
  getInitials,
  getChannelIcon,
  getStatusChip,
} from "../utils/utils";
import {
  getClientCommercialName,
  getRazaoSocial,
  isPessoaJuridica,
} from "../utils/clientName";
import ClientRelationshipBadge from "./Chat/ClientRelationshipBadge";
import { getClientListRelationshipProps } from "../utils/clientRelationships";
import { findUserDmSpace } from "../pages/Team/utils/teamUiUtils";
import {
  isPopupEquipePath,
  POPUP_TEAM_BASE,
  resolveTeamBaseFromPathname,
  serviceChatPath,
  teamPath,
  teamSalesPipelinePaths,
  teamTasksPaths,
} from "../utils/teamNavigation";
import { normalizeStatusInput } from "./StatusChipSelector";
import { useAuthContext } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { sanitizeTelephonyListPreview } from "../utils/partnerApiAuditDisplay";

const DEFAULT_RECENT_USERS = 3;
const DEFAULT_RECENT_CLIENTS = 3;
const RECENT_STORE_LIMIT = 12;

function openAppPathInNewTab(path) {
  const url = `${window.location.origin}${path.startsWith("/") ? path : `/${path}`}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

function unwrapUserRecord(res) {
  let row = res?.data ?? res;
  if (row && typeof row === "object" && "data" in row && !Array.isArray(row)) row = row.data;
  return row && typeof row === "object" ? row : null;
}

function toInteractedAt(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function rememberRecentEntity(storageKey, entity) {
  const id = Number(entity?.id);
  if (!Number.isFinite(id) || id <= 0) return false;
  const prev = readWorkspaceLocalJson(storageKey, []);
  const list = Array.isArray(prev) ? prev : [];
  const next = [
    {
      id,
      nome: entity?.nome || entity?.name || "",
      name: entity?.nome || entity?.name || "",
      email: entity?.email || "",
      imagem: entity?.imagem || entity?.profile_picture || null,
      interactedAt: Date.now(),
    },
    ...list.filter((item) => Number(item?.id) !== id),
  ].slice(0, RECENT_STORE_LIMIT);
  writeWorkspaceLocalJson(storageKey, next);
  return true;
}

function mergeRecentEntities(lists, { excludeId = null, limit = 3 } = {}) {
  const map = new Map();
  for (const list of lists) {
    for (const item of list || []) {
      const id = Number(item?.id);
      if (!Number.isFinite(id) || id <= 0) continue;
      if (excludeId != null && id === Number(excludeId)) continue;
      const interactedAt = toInteractedAt(item?.interactedAt);
      const prev = map.get(id);
      if (!prev || interactedAt >= toInteractedAt(prev.interactedAt)) {
        map.set(id, {
          id,
          nome: item?.nome || item?.name || prev?.nome || "",
          name: item?.nome || item?.name || prev?.name || "",
          email: item?.email || prev?.email || "",
          imagem: item?.imagem || item?.profile_picture || prev?.imagem || null,
          interactedAt,
        });
      } else if (prev) {
        map.set(id, {
          ...prev,
          email: prev.email || item?.email || "",
          imagem: prev.imagem || item?.imagem || item?.profile_picture || null,
          nome: prev.nome || item?.nome || item?.name || "",
          name: prev.name || item?.nome || item?.name || "",
        });
      }
    }
  }
  return [...map.values()]
    .sort((a, b) => toInteractedAt(b.interactedAt) - toInteractedAt(a.interactedAt))
    .slice(0, limit);
}

const getUserAvatarUrl = (user) => {
  const u = user?.imagem;
  return typeof u === "string" && u.trim().length > 0 ? u : null;
};

const defaultNewEventRange = () => {
  const start = new Date();
  const end = dayjs(start).add(1, "hour").toDate();
  return { start, end, allDay: false };
};

const sanitizeText = (text) => {
  if (!text) return "";
  const div = document.createElement("div");
  div.innerHTML = text;
  return (div.textContent || div.innerText || "").trim();
};

const formatCurrencyBRL = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "R$ 0,00";
  return num.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

/** Extrai mensagem de erro da API (modelo fornecido) */
const extractApiError = (e, fallbackMsg) =>
  (e && e.response && e.response.data && e.response.data.message) ||
  e?.message ||
  fallbackMsg;

/** Chip de responsável com o mesmo padrão visual da lista de solicitações */
const renderAssigneeChip = (normalizedAssigned, userData, theme, status, tShell) => {
  const statusValue = normalizeStatusInput(status);
  const isClosed =
    statusValue === 2 || statusValue === 4 || statusValue === 5;
  const isQueue = !normalizedAssigned;
  const isMine =
    !!normalizedAssigned &&
    !!userData?.nome &&
    String(normalizedAssigned).trim() === String(userData.nome).trim();

  let bg;
  let color = theme.palette.common.white;
  let label;

  if (isQueue) {
    if (isClosed) {
      bg = theme.palette.error.light || "#ef9a9a";
      label = tShell("search.assigneeNoUser");
      color = theme.palette.getContrastText
        ? theme.palette.getContrastText(bg)
        : theme.palette.text.primary;
    } else {
      bg = theme.palette.grey[800];
      label = tShell("search.assigneeQueue");
    }
  } else if (isMine) {
    bg = theme.palette.success.main;
    label = tShell("search.assigneeMine");
  } else {
    bg = theme.palette.info.main;
    label = normalizedAssigned;
  }

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        backgroundColor: bg,
        color,
        fontSize: "0.75rem",
        height: "24px",
        maxWidth: "220px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    />
  );
};

/** Monta trilha de Tipo/Subtipos (tipo • subtipo1 • subtipo2 • subtipo3) */
const buildSaleTypePath = (sale) => {
  if (!sale) return "";
  const parts = [];

  const typeName = sale.typeName || sale.tipo_nome;
  const s1Name = sale.subtype1Name || sale.subtipo1_nome;
  const s2Name = sale.subtype2Name || sale.subtipo2_nome;
  const s3Name = sale.subtype3Name || sale.subtipo3_nome;

  if (typeName) parts.push(typeName);
  if (s1Name) parts.push(s1Name);
  if (s2Name) parts.push(s2Name);
  if (s3Name) parts.push(s3Name);

  if (parts.length) return parts.join(" • ");

  const ids = [];
  const typeId = sale.typeId ?? sale.id_tipo;
  const s1Id = sale.subtype1Id ?? sale.id_subtipo1;
  const s2Id = sale.subtype2Id ?? sale.id_subtipo2;
  const s3Id = sale.subtype3Id ?? sale.id_subtipo3;

  if (typeId != null) ids.push(`T${typeId}`);
  if (s1Id != null) ids.push(`S1-${s1Id}`);
  if (s2Id != null) ids.push(`S2-${s2Id}`);
  if (s3Id != null) ids.push(`S3-${s3Id}`);

  return ids.join(" • ");
};

const HELP_TOPICS = [
  {
    id: "help-automation-rules",
    title: "Como criar uma regra de automação?",
    keywords: ["automação", "automatizar", "regra", "regras", "fluxo automático"],
    route: "/settings/automation-rules",
    summary:
      "Use as Regras de Automação para disparar mensagens, atribuir responsáveis ou atualizar status de forma automática.",
  },
  {
    id: "help-connect-whatsapp",
    title: "Como conectar o canal WhatsApp?",
    keywords: ["whatsapp", "canal", "conectar whatsapp", "configurar whatsapp"],
    route: "/settings/channels",
    summary:
      "Vá em Canais nas Configurações para conectar ou revisar a integração com WhatsApp e outros canais.",
  },
  {
    id: "help-first-conversation",
    title: "Como atender minha primeira solicitação?",
    keywords: ["primeira solicitação", "primeiro atendimento", "atender cliente", "chat"],
    route: "/chat",
    summary:
      "Abra a tela de Chat para visualizar a fila de solicitações e iniciar o atendimento ao cliente.",
  },
  {
    id: "help-bpe",
    title: "Como usar o recurso BPe na solicitação?",
    keywords: ["bpe", "bilhete", "bilhete eletrônico", "rodoviário"],
    route: "/chat",
    summary:
      "Dentro de uma solicitação elegível, use o assistente BPe para consultar, emitir ou gerenciar bilhetes eletrônicos.",
  },
  {
    id: "help-pipeline",
    title: "O que é o Pipeline de Clientes?",
    keywords: ["pipeline", "etapas", "funil", "leads", "vendas"],
    route: "/pipeline",
    summary:
      "O Pipeline organiza seus leads e clientes por etapa do funil, ajudando a priorizar atendimentos e oportunidades.",
  },
];

const looksLikeQuestion = (text) => {
  const q = (text || "").toLowerCase().trim();
  if (!q) return false;
  if (q.includes("?")) return true;
  return (
    q.startsWith("como ") ||
    q.startsWith("o que ") ||
    q.startsWith("onde ") ||
    q.startsWith("por que") ||
    q.startsWith("porque ") ||
    q.startsWith("pra que") ||
    q.startsWith("para que") ||
    q.startsWith("duvida") ||
    q.startsWith("dúvida")
  );
};

const sortConversationsByQuery = (conversations, query) => {
  const raw = (query || "").toLowerCase().trim();
  const term = isProtocolSearchQuery(raw)
    ? normalizeProtocolSearchQuery(raw).toLowerCase()
    : raw;
  if (!term) return conversations;

  const isNumeric = /^\d+$/.test(term);

  const score = (c) => {
    const name = (c?.name || "").toLowerCase();
    const lastMessage = (c?.lastMessage || "").toLowerCase();
    const idStr = String(c?.id || "");

    if (isNumeric && idStr === term) return 0;
    if (name.startsWith(term)) return 1;
    if (name.includes(term)) return 2;
    if (lastMessage.includes(term)) return 5;
    return 10;
  };

  return [...conversations].sort((a, b) => {
    const sa = score(a);
    const sb = score(b);
    if (sa !== sb) return sa - sb;
    return 0;
  });
};

/* ================= Componente ================= */
const SearchAutocomplete = ({
  autoFocus = false,
  dialogMode = false,
  onRequestClose = null,
} = {}) => {
  const { t, i18n } = useTranslation("common");
  const { t: ts } = useTranslation("shell");
  const theme = useTheme();
  const dateLocale = i18n.language?.startsWith("en") ? "en-US" : "pt-BR";
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(Boolean(dialogMode));
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

  const [searchHistory, setSearchHistory] = useState(() =>
    readWorkspaceSessionJson(WS_KEY.SEARCH_HISTORY, [])
  );
  const [recentInteractionTick, setRecentInteractionTick] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const inCockpit = isPopupEquipePath(location.pathname);
  const teamBasePath = resolveTeamBaseFromPathname(location.pathname);
  const salesPaths = useMemo(
    () => teamSalesPipelinePaths(teamBasePath),
    [teamBasePath]
  );
  const tasksPaths = useMemo(() => teamTasksPaths(teamBasePath), [teamBasePath]);

  const bumpRecentInteractions = useCallback(() => {
    setRecentInteractionTick((n) => n + 1);
  }, []);

  const rememberUserInteraction = useCallback(
    (user) => {
      if (rememberRecentEntity(WS_KEY.SEARCH_RECENT_USERS, user)) {
        bumpRecentInteractions();
      }
    },
    [bumpRecentInteractions]
  );

  const rememberClientInteraction = useCallback(
    (client) => {
      if (rememberRecentEntity(WS_KEY.SEARCH_RECENT_CLIENTS, client)) {
        bumpRecentInteractions();
      }
    },
    [bumpRecentInteractions]
  );

  const finishNavigation = useCallback(() => {
    setShowSuggestions(false);
    if (typeof onRequestClose === "function") onRequestClose();
  }, [onRequestClose]);

  /** No cockpit, rotas fora do shell /popup-equipe abrem em nova aba. */
  const goToPath = useCallback(
    (path, { externalInCockpit = false } = {}) => {
      if (!path) return;
      if (inCockpit && externalInCockpit) {
        openAppPathInNewTab(path);
        finishNavigation();
        return;
      }
      navigate(path);
      finishNavigation();
    },
    [finishNavigation, inCockpit, navigate]
  );

  const goToSearchResults = useCallback(
    (searchPath) => {
      if (inCockpit) {
        openAppPathInNewTab(searchPath);
        finishNavigation();
        return;
      }
      navigate(searchPath);
      finishNavigation();
    },
    [finishNavigation, inCockpit, navigate]
  );

  const goToConversation = useCallback(
    (conversationId) => {
      if (conversationId == null) return;
      if (inCockpit) {
        goToPath(serviceChatPath(conversationId, POPUP_TEAM_BASE));
        return;
      }
      goToPath(`/chat/${conversationId}`);
    },
    [goToPath, inCockpit]
  );

  const goToSale = useCallback(
    (saleId) => {
      if (saleId == null) return;
      if (inCockpit) {
        goToPath(salesPaths.lead(saleId));
        return;
      }
      goToPath(`/sales-pipeline/${saleId}`);
    },
    [goToPath, inCockpit, salesPaths]
  );

  const goToClient = useCallback(
    (clientId, clientMeta = null) => {
      if (clientId == null) return;
      rememberClientInteraction({
        id: clientId,
        nome: clientMeta?.nome || clientMeta?.name || "",
        email: clientMeta?.email || "",
        imagem: clientMeta?.imagem || clientMeta?.profile_picture || null,
      });
      goToPath(`/clients/${clientId}`, { externalInCockpit: true });
    },
    [goToPath, rememberClientInteraction]
  );

  const goToHelpTopic = useCallback(
    (route) => {
      if (!route) return;
      goToPath(route, { externalInCockpit: true });
    },
    [goToPath]
  );

  const searchResultsFilterState = useMemo(() => {
    if (!location.pathname.includes("/search-results")) return null;
    return parseSearchParamsFromUrl(location.search);
  }, [location.pathname, location.search]);

  const advancedSearchInitial = useMemo(() => {
    if (!searchResultsFilterState) return filterStateToModalInitial(EMPTY_SEARCH_FILTER_STATE);
    return filterStateToModalInitial(searchResultsFilterState);
  }, [searchResultsFilterState]);

  const advancedSearchInitialQuery = searchResultsFilterState?.q?.trim() || "";

  useEffect(() => {
    if (!location.pathname.includes("/search-results")) return;
    const { q } = parseSearchParamsFromUrl(location.search);
    setQuery(typeof q === "string" ? q : "");
  }, [location.pathname, location.search]);

  const [startError, setStartError] = useState(null);
  const [startingByClient, setStartingByClient] = useState({});
  const [openingDmByUser, setOpeningDmByUser] = useState({});
  const [agendaDialogOpen, setAgendaDialogOpen] = useState(false);
  const [agendaDefaultRange, setAgendaDefaultRange] = useState(null);
  const [agendaDialogError, setAgendaDialogError] = useState("");
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskDialogUser, setTaskDialogUser] = useState(null);
  const [creatingTask, setCreatingTask] = useState(false);
  const [resolvedEmails, setResolvedEmails] = useState({});
  const resolvedEmailsRef = useRef({});

  const lookupUserEmail = useCallback(async (user) => {
    const direct = String(user?.email || "").trim();
    if (direct) return direct;
    const id = Number(user?.id);
    if (!Number.isFinite(id) || id <= 0) return "";
    if (Object.prototype.hasOwnProperty.call(resolvedEmailsRef.current, id)) {
      return resolvedEmailsRef.current[id];
    }
    try {
      const res = await fetchData(`/users/${id}`, "GET");
      const row = unwrapUserRecord(res);
      const email = String(row?.email || "").trim();
      resolvedEmailsRef.current[id] = email;
      setResolvedEmails((prev) => (prev[id] === email ? prev : { ...prev, [id]: email }));
      return email;
    } catch (error) {
      console.error("Erro ao obter e-mail do usuário:", error);
      return null;
    }
  }, []);

  const { registerFeatureUse } = useOnboarding();
  const { userData } = useAuthContext();
  const isMasterUser = Number(userData?.master) === 1;
  const myUserId = Number(userData?.id);

  /** Data hooks */
  const normalizedTerm = (debouncedQuery || "").trim();
  const hasSearchTerm = normalizedTerm.length >= 2;
  const isDefaultSuggestions = normalizedTerm.length === 0;
  const protocolQuery = isProtocolSearchQuery(normalizedTerm);
  const conversationsSearchTerm = protocolQuery
    ? normalizeProtocolSearchQuery(normalizedTerm)
    : debouncedQuery;

  // Default (sem texto): só recentes via DMs/solicitações — sem listagem genérica de users/clients.
  // Com termo (>=2): busca normal nas APIs.
  const shouldQueryDirectoryApi = showSuggestions && hasSearchTerm;
  const shouldQueryConversationsApi =
    showSuggestions && (isDefaultSuggestions || hasSearchTerm);

  const { data: clientData, isLoading: loadingClients } = useClients(
    1,
    10,
    debouncedQuery,
    null,
    { enabled: shouldQueryDirectoryApi }
  );

  const { data: conversationData, isLoading: loadingConversations } = useConversations(
    conversationsSearchTerm,
    [],
    {
      default_date_window_days: 30,
      // Protocolo: Atendimento + Compras (API também ignora tipo_scope no lookup por ID).
      ...(protocolQuery ? { tipo_scope: "todos" } : {}),
      ...(isDefaultSuggestions ? { limit: 30 } : {}),
    },
    false,
    { enabled: shouldQueryConversationsApi }
  );

  const { data: usersData, isLoading: loadingUsers } = useUsers(
    1,
    10,
    debouncedQuery,
    "humanos",
    { enabled: shouldQueryDirectoryApi, order: "nome" }
  );

  const { spaces, createDm, isLoading: loadingSpaces } = useTeamSpaces();

  const recentUsersFromDms = useMemo(() => {
    return (spaces || [])
      .filter(
        (space) =>
          space?.type === "dm" &&
          space?.dmPeerType === "user" &&
          Number(space?.dmPeerUserId) > 0
      )
      .map((space) => ({
        id: Number(space.dmPeerUserId),
        nome: space.dmPeerName || "",
        name: space.dmPeerName || "",
        email: space.dmPeerEmail || "",
        imagem: space.dmPeerAvatarUrl || null,
        interactedAt: toInteractedAt(space.lastMessageAt || space.updatedAt),
      }));
  }, [spaces]);

  const recentClientsFromConversations = useMemo(() => {
    const raw =
      conversationData?.pages?.flatMap((page) => page?.list || []) || [];
    const sorted = [...raw].sort(
      (a, b) =>
        toInteractedAt(b?.timestamp || b?.lastUpdate || b?.lastClientMessageAt) -
        toInteractedAt(a?.timestamp || a?.lastUpdate || a?.lastClientMessageAt)
    );
    const out = [];
    const seen = new Set();
    for (const conv of sorted) {
      const id = Number(conv?.clientId);
      if (!Number.isFinite(id) || id <= 0 || seen.has(id)) continue;
      seen.add(id);
      out.push({
        id,
        nome: conv?.name || "",
        name: conv?.name || "",
        email: conv?.email || "",
        imagem: conv?.profile_picture || null,
        interactedAt: toInteractedAt(
          conv?.timestamp || conv?.lastUpdate || conv?.lastClientMessageAt
        ),
      });
    }
    return out;
  }, [conversationData]);

  const defaultRecentUsers = useMemo(() => {
    const stored = readWorkspaceLocalJson(WS_KEY.SEARCH_RECENT_USERS, []);
    return mergeRecentEntities([recentUsersFromDms, stored], {
      excludeId: myUserId,
      limit: DEFAULT_RECENT_USERS,
    });
  }, [recentUsersFromDms, myUserId, recentInteractionTick]);

  const defaultRecentClients = useMemo(() => {
    const stored = readWorkspaceLocalJson(WS_KEY.SEARCH_RECENT_CLIENTS, []);
    return mergeRecentEntities([recentClientsFromConversations, stored], {
      limit: DEFAULT_RECENT_CLIENTS,
    });
  }, [recentClientsFromConversations, recentInteractionTick]);
  const { data: agendaCalendarsData } = useUserAgendaCalendars({
    enabled: agendaDialogOpen,
  });
  const agendaCalendars = agendaCalendarsData?.calendars || [];
  const defaultAgendaCalendarId =
    agendaCalendars.find((c) => c?.isDefault || c?.is_default)?.id ||
    agendaCalendars[0]?.id ||
    "";
  const { createMutation: createAgendaMutation } = useUserAgendaMutations();


  /** Vendas (pipeline) */
  const [salesData, setSalesData] = useState([]);
  const [loadingSales, setLoadingSales] = useState(false);
  const [taskHits, setTaskHits] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);

  /** Debounce input */
  const debounceSearch = useCallback(
    debounce((value) => setDebouncedQuery(value), 300),
    []
  );

  /** Handlers input */
  const handleInputChange = (e) => {
    const value = e.target.value || "";
    setQuery(value);
    setShowSuggestions(true);
    debounceSearch(value);
  };

  const handleSearch = () => {
    if (query.trim()) {
      const qForNav = isProtocolSearchQuery(query)
        ? normalizeProtocolSearchQuery(query)
        : query.trim();
      const updatedHistory = [...new Set([qForNav, ...(searchHistory || [])])].slice(0, 4);
      setSearchHistory(updatedHistory);
      writeWorkspaceSessionJson(WS_KEY.SEARCH_HISTORY, updatedHistory);
      registerFeatureUse("global-search");
      goToSearchResults(`/search-results?q=${encodeURIComponent(qForNav)}`);
    }
  };

  /** Fechar dropdown ao clicar fora */
  const handleClickOutside = (event) => {
    if (
      autocompleteRef.current &&
      !autocompleteRef.current.contains(event.target) &&
      event.target !== autocompleteRef.current.querySelector("input")
    ) {
      setShowSuggestions(false);
    }
  };

  const handleInputFocus = () => {
    setShowSuggestions(true);
    if (!query.trim()) debounceSearch("");
  };

  const matchedHelpTopics = useMemo(() => {
    const term = (debouncedQuery || "").toLowerCase().trim();
    if (!term) return [];

    const isQuestion = looksLikeQuestion(term);

    return HELP_TOPICS.filter((topic) => {
      const inTitle = topic.title.toLowerCase().includes(term);
      const inKeywords = (topic.keywords || []).some((k) =>
        k.toLowerCase().includes(term)
      );
      if (!inTitle && !inKeywords) return false;
      if (!isQuestion) {
        return inTitle || topic.keywords.some((k) =>
          term.split(/\s+/).some((word) => word && k.toLowerCase().includes(word))
        );
      }
      return true;
    }).slice(0, 3);
  }, [debouncedQuery]);

  /** Criar nova solicitação (RELACIONAMENTO) e abrir popup */
  const handleNewConversation = async (clientId) => {
    if (!clientId) return;

    setStartingByClient((prev) => ({ ...prev, [clientId]: true }));

    try {
      const selected = getSelectedCompanyIds();
      const projectId = selected.length ? Number(selected[0]) : undefined;

      const payload = {
        projectId: projectId || undefined,
        clientId,
        client: { id: clientId },
        typeId: undefined,
        typeKind: "1", // relacionamento
        initialMessage: "",
        source: "search-autocomplete",
      };

      const created = await fetchData("/conversations", "POST", payload);
      const conv = created?.data || created;
      if (!conv?.id) throw new Error("A API não retornou o ID da solicitação criada.");

      rememberClientInteraction({ id: clientId });

      const width = window.screen.width;
      const height = window.screen.height;
      window.open(
        `/popup-call/${conv.id}`,
        "SolicitaçãoPopup",
        `width=${width},height=${height},top=0,left=0,scrollbars=no,toolbar=no,location=no,status=no,menubar=no`
      );
      finishNavigation();
    } catch (error) {
      console.error("Erro ao criar nova solicitação:", error);
      setStartError(extractApiError(error, ts("search.startRequestError")));
    } finally {
      setStartingByClient((prev) => {
        const next = { ...prev };
        delete next[clientId];
        return next;
      });
    }
  };

  const handleScheduleWithUser = async (user) => {
    const resolved = await lookupUserEmail(user);
    if (resolved == null) {
      setStartError(ts("search.agendaSaveError"));
      return;
    }
    const email = String(resolved).trim().toLowerCase();
    if (!email) {
      setStartError(ts("search.userNoEmail"));
      return;
    }
    rememberUserInteraction({ ...user, email });
    const name = String(user?.nome || user?.name || "").trim();
    setAgendaDialogError("");
    setAgendaDefaultRange({
      ...defaultNewEventRange(),
      title: name ? ts("search.meetingWith", { name }) : "",
      attendees: [{ email, ...(name ? { name } : {}) }],
      calendarId: defaultAgendaCalendarId || undefined,
    });
    setAgendaDialogOpen(true);
    setShowSuggestions(false);
  };

  const handleAgendaSave = async (payload) => {
    setAgendaDialogError("");
    try {
      await createAgendaMutation.mutateAsync(payload);
      setAgendaDialogOpen(false);
      setAgendaDefaultRange(null);
    } catch (error) {
      setAgendaDialogError(extractApiError(error, ts("search.agendaSaveError")));
    }
  };

  const handleOpenUserDm = async (user) => {
    const uid = Number(user?.id);
    if (!Number.isFinite(uid) || uid <= 0) return;
    if (uid === myUserId) {
      setStartError(ts("search.dmSelfError"));
      return;
    }
    if (isMasterUser) {
      setStartError(ts("search.dmMasterError"));
      return;
    }

    setOpeningDmByUser((prev) => ({ ...prev, [uid]: true }));
    try {
      rememberUserInteraction(user);
      const existing = findUserDmSpace(spaces, uid);
      if (existing?.id) {
        goToPath(teamPath(existing.id, teamBasePath));
        return;
      }
      const res = await createDm({ userId: uid });
      const spaceId = res?.id || res?.space?.id;
      if (!spaceId) throw new Error("A API não retornou o ID da conversa.");
      goToPath(teamPath(spaceId, teamBasePath));
    } catch (error) {
      console.error("Erro ao abrir conversa interna:", error);
      setStartError(extractApiError(error, ts("search.dmOpenError")));
    } finally {
      setOpeningDmByUser((prev) => {
        const next = { ...prev };
        delete next[uid];
        return next;
      });
    }
  };

  const handleOpenCreateTask = (user) => {
    rememberUserInteraction(user);
    setTaskDialogUser(user || null);
    setTaskDialogOpen(true);
    setShowSuggestions(false);
  };

  const handleCreateTaskSubmit = async ({ title, boardId, columnId, assignees }) => {
    if (!boardId || !columnId || !title?.trim()) return false;
    setCreatingTask(true);
    try {
      await createTeamBoardCard(boardId, {
        columnId,
        title: title.trim(),
        assignees: Array.isArray(assignees) ? assignees : [],
      });
      setTaskDialogOpen(false);
      setTaskDialogUser(null);
      goToPath(tasksPaths.board(boardId));
      return true;
    } catch (error) {
      console.error("Erro ao criar tarefa:", error);
      setStartError(extractApiError(error, ts("search.taskCreateError")));
      return false;
    } finally {
      setCreatingTask(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!dialogMode) return;
    setShowSuggestions(true);
    if (!query.trim()) debounceSearch("");
    const focusTimer = window.setTimeout(() => {
      inputRef.current?.focus?.();
    }, 50);
    return () => window.clearTimeout(focusTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- abrir sugestões só ao montar o dialog
  }, [dialogMode]);

  /** ID de tarefa (#123, 123 ou tarefa-123) na busca geral. */
  useEffect(() => {
    let cancelled = false;
    const taskId = parseTaskIdSearchQuery(debouncedQuery);
    if (!showSuggestions || !taskId) {
      setTaskHits([]);
      setLoadingTasks(false);
      return undefined;
    }

    setLoadingTasks(true);
    fetchTeamBoardCardLocation(taskId)
      .then((data) => {
        if (cancelled) return;
        const cardId = Number(data?.cardId);
        const boardId = Number(data?.boardId);
        if (!Number.isFinite(cardId) || cardId <= 0 || !Number.isFinite(boardId) || boardId <= 0) {
          setTaskHits([]);
          return;
        }
        setTaskHits([
          {
            cardId,
            boardId,
            title: data?.title || "",
            boardName: data?.boardName || "",
          },
        ]);
      })
      .catch(() => {
        if (!cancelled) setTaskHits([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingTasks(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, showSuggestions]);

  const handleOpenTaskHit = (task) => {
    if (!task?.boardId || !task?.cardId) return;
    openTeamTasksPopupWindow({ boardId: task.boardId, cardId: task.cardId });
    finishNavigation();
  };

  /** Buscar vendas (pipeline) sempre que o termo debounced mudar */
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const term = (debouncedQuery || "").trim();
      if (!term || term.length < 2) {
        setSalesData([]);
        setLoadingSales(false);
        return;
      }

      setLoadingSales(true);
      try {
        let idsProjetosParam = "";
        try {
          const arr = getSelectedCompanyIds();
          if (arr.length > 0) {
            idsProjetosParam = arr.join(",");
          }
        } catch {
          /* ignore */
        }

        const params = new URLSearchParams();
        params.set("page", "1");
        params.set("limit", "5");
        params.set("search", term);
        if (idsProjetosParam) params.set("ids_projetos", idsProjetosParam);

        const resp = await fetchData(`/sales-pipeline/leads?${params.toString()}`, "GET");
        const list = resp?.data?.data || resp?.data || [];

        if (!cancelled) {
          setSalesData(Array.isArray(list) ? list : []);
        }
      } catch (err) {
        console.error("Erro ao carregar vendas do pipeline:", err);
        if (!cancelled) {
          setSalesData([]);
        }
      } finally {
        if (!cancelled) setLoadingSales(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  /** Distribuição e normalização dos resultados */
  const distributeResults = () => {
    if (isDefaultSuggestions) {
      return {
        history: [],
        conversations: [],
        clients: defaultRecentClients,
        users: defaultRecentUsers,
        sales: [],
        helpResults: [],
      };
    }

    const maxResults = 14;
    const maxHistory = 3;
    const maxConversations = 3;
    const maxClients = 2;
    const maxUsers = 2;
    const maxSales = 2;

    const filteredHistory = (searchHistory || []).filter((item) =>
      (item || "").toLowerCase().includes((query || "").toLowerCase())
    );

    const rawConversations = conversationData?.pages?.flatMap((page) => page?.list || []) || [];
    const allConversations = sortConversationsByQuery(rawConversations, debouncedQuery || query);
    const rawClients = clientData?.clients;
    const allClients = Array.isArray(rawClients)
      ? rawClients
      : Array.isArray(rawClients?.data)
      ? rawClients.data
      : [];
    const rawUsers = usersData?.users;
    const allUsers = (Array.isArray(rawUsers) ? rawUsers : []).filter((u) => {
      const id = Number(u?.id);
      if (!Number.isFinite(id) || id <= 0) return false;
      if (Number.isFinite(myUserId) && myUserId > 0 && id === myUserId) return false;
      return true;
    });
    const allSales = salesData || [];
    const help = matchedHelpTopics;

    let remaining = maxResults;

    // Prioridade de slots: usuários → clientes → solicitações → demais
    let users = allUsers.slice(0, Math.min(maxUsers, remaining));
    remaining -= users.length;

    let clients = allClients.slice(0, Math.min(maxClients, remaining));
    remaining -= clients.length;

    let conversations = allConversations.slice(0, Math.min(maxConversations, remaining));
    remaining -= conversations.length;

    let history = filteredHistory.slice(0, Math.min(maxHistory, remaining));
    remaining -= history.length;

    let sales = allSales.slice(0, Math.min(maxSales, remaining));
    remaining -= sales.length;

    let helpResults = help.slice(0, Math.min(3, remaining));
    remaining -= helpResults.length;

    if (remaining > 0) {
      const addUsers = allUsers.slice(users.length, users.length + remaining);
      users = users.concat(addUsers);
      remaining -= addUsers.length;
    }
    if (remaining > 0) {
      const addClients = allClients.slice(clients.length, clients.length + remaining);
      clients = clients.concat(addClients);
      remaining -= addClients.length;
    }
    if (remaining > 0) {
      const addConvs = allConversations.slice(conversations.length, conversations.length + remaining);
      conversations = conversations.concat(addConvs);
      remaining -= addConvs.length;
    }
    if (remaining > 0) {
      const addSales = allSales.slice(sales.length, sales.length + remaining);
      sales = sales.concat(addSales);
      remaining -= addSales.length;
    }
    if (remaining > 0) {
      const addHistory = filteredHistory.slice(history.length, history.length + remaining);
      history = history.concat(addHistory);
      remaining -= addHistory.length;
    }
    if (remaining > 0 && helpResults.length < help.length) {
      const addHelp = help.slice(helpResults.length, helpResults.length + remaining);
      helpResults = helpResults.concat(addHelp);
    }

    return { history, conversations, clients, users, sales, helpResults };
  };

  const { history, conversations, clients, users, sales, helpResults } = distributeResults();

  const missingEmailIdsKey = useMemo(() => {
    return users
      .map((user) => {
        const id = Number(user?.id);
        if (!Number.isFinite(id) || id <= 0) return null;
        if (String(user?.email || "").trim()) return null;
        if (Object.prototype.hasOwnProperty.call(resolvedEmails, id)) return null;
        return id;
      })
      .filter((id) => id != null)
      .join(",");
  }, [users, resolvedEmails]);

  useEffect(() => {
    if (!missingEmailIdsKey) return undefined;
    missingEmailIdsKey.split(",").forEach((id) => {
      lookupUserEmail({ id });
    });
    return undefined;
  }, [missingEmailIdsKey, lookupUserEmail]);
  const isLoading = isDefaultSuggestions
    ? loadingConversations || loadingSpaces
    : loadingClients || loadingConversations || loadingSales || loadingUsers || loadingTasks;
  const hasAnyData =
    history.length > 0 ||
    conversations.length > 0 ||
    clients.length > 0 ||
    users.length > 0 ||
    sales.length > 0 ||
    helpResults.length > 0 ||
    taskHits.length > 0;
  const showInitialLoader = isLoading && !hasAnyData;

  // Ordem visual/teclado: usuários → clientes → solicitações → histórico → vendas → ajuda
  const suggestionIndexOffsets = useMemo(
    () => ({
      tasks: 0,
      users: taskHits.length,
      clients: taskHits.length + users.length,
      conversations: taskHits.length + users.length + clients.length,
      history: taskHits.length + users.length + clients.length + conversations.length,
      sales: taskHits.length + users.length + clients.length + conversations.length + history.length,
      help:
        taskHits.length +
        users.length +
        clients.length +
        conversations.length +
        history.length +
        sales.length,
    }),
    [
      taskHits.length,
      users.length,
      clients.length,
      conversations.length,
      history.length,
      sales.length,
    ]
  );

  /** Navegação por teclado nas listas */
  const totalItems =
    taskHits.length +
    history.length +
    conversations.length +
    clients.length +
    users.length +
    sales.length +
    helpResults.length;

  const handleKeyDown = (e) => {
    if (totalItems === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % totalItems);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + totalItems) % totalItems);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex === -1) {
        handleSearch();
      } else {
        const tk = taskHits.length;
        const u = users.length;
        const cl = clients.length;
        const c = conversations.length;
        const h = history.length;
        const s = sales.length;

        if (selectedIndex < tk) {
          handleOpenTaskHit(taskHits[selectedIndex]);
          return;
        }
        if (selectedIndex < tk + u) {
          handleOpenUserDm(users[selectedIndex - tk]);
          return;
        }
        if (selectedIndex < tk + u + cl) {
          const client = clients[selectedIndex - tk - u];
          goToClient(client.id, client);
          return;
        }
        if (selectedIndex < tk + u + cl + c) {
          goToConversation(conversations[selectedIndex - tk - u - cl].id);
          return;
        }
        if (selectedIndex < tk + u + cl + c + h) {
          goToSearchResults(
            `/search-results?q=${encodeURIComponent(history[selectedIndex - tk - u - cl - c])}`
          );
          return;
        }
        if (selectedIndex < tk + u + cl + c + h + s) {
          goToSale(sales[selectedIndex - tk - u - cl - c - h].id);
          return;
        }
        const topic = helpResults[selectedIndex - tk - u - cl - c - h - s];
        if (topic?.route) {
          goToHelpTopic(topic.route);
        }
      }
    }
  };

  useEffect(() => {
    const total =
      taskHits.length +
      history.length +
      conversations.length +
      clients.length +
      users.length +
      sales.length +
      helpResults.length;
    if (selectedIndex >= total) setSelectedIndex(-1);
  }, [
    taskHits.length,
    history.length,
    conversations.length,
    clients.length,
    users.length,
    sales.length,
    helpResults.length,
    selectedIndex,
  ]);

  /** Datas seguras */
  const safeTimestamp = (conv) => {
    const raw =
      conv?.timestamp ||
      conv?.lastUpdate ||
      conv?.data_ultima_atualizacao ||
      null;
    if (!raw) return "—";
    const d = new Date(raw);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString(dateLocale);
  };

  const safeSalesDate = (lead) => {
    const raw = lead?.createdAtLabel || lead?.createdAt || null;
    if (!raw) return "—";
    const d = new Date(raw);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString(dateLocale);
  };

  return (
    <Box ref={autocompleteRef} sx={{ position: "relative", width: "100%" }}>
      {showAdvancedSearch && (
        <AdvancedSearchModal
          open={showAdvancedSearch}
          onClose={() => setShowAdvancedSearch(false)}
          initialQuery={advancedSearchInitialQuery}
          initialAdvanced={advancedSearchInitial}
          onApply={(payload) => {
            const state = modalPayloadToFilterState(payload);
            setShowAdvancedSearch(false);
            goToSearchResults(`/search-results?${filterStateToUrlString(state)}`);
          }}
        />
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "8px",
          padding: "4px 12px",
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Search sx={{ marginRight: 1, color: theme.palette.text.secondary }} />
        <InputBase
          inputRef={inputRef}
          autoFocus={autoFocus || dialogMode}
          placeholder={t("searchPlaceholder")}
          fullWidth
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={(e) => {
            if (dialogMode && e.key === "Escape") {
              e.preventDefault();
              e.stopPropagation();
              if (typeof onRequestClose === "function") onRequestClose();
              return;
            }
            handleKeyDown(e);
          }}
          sx={{ fontSize: "16px", color: theme.palette.text.primary }}
        />
        <IconButton
          onClick={() => setShowAdvancedSearch(true)}
          size="small"
          aria-label={ts("search.advancedSearchAria")}
        >
          <Tune />
        </IconButton>
      </Box>

      {showSuggestions && (
        <Box
          sx={{
            position: dialogMode ? "relative" : "absolute",
            top: dialogMode ? "auto" : "48px",
            left: 0,
            right: 0,
            mt: dialogMode ? 1 : 0,
            backgroundColor: theme.palette.background.paper,
            boxShadow: dialogMode ? "none" : theme.shadows[4],
            border: dialogMode ? `1px solid ${theme.palette.divider}` : "none",
            borderRadius: "8px",
            zIndex: dialogMode ? "auto" : 10,
            maxHeight: dialogMode ? "min(70vh, 520px)" : "400px",
            overflowY: "auto",
          }}
        >
          {showInitialLoader ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {taskHits.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ px: 2, pt: 1, color: theme.palette.text.secondary }}>
                    {ts("search.tasks")}
                  </Typography>
                  {taskHits.map((task, i) => {
                    const thisIndex = suggestionIndexOffsets.tasks + i;
                    return (
                      <ListItem
                        key={`task-${task.cardId}`}
                        onClick={() => handleOpenTaskHit(task)}
                        selected={selectedIndex === thisIndex}
                        sx={{
                          cursor: "pointer",
                          backgroundColor:
                            selectedIndex === thisIndex ? theme.palette.action.selected : "transparent",
                          "&:hover": { backgroundColor: theme.palette.action.hover },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                            <AssignmentOutlined fontSize="small" />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={formatTaskDisplayName(task.cardId, task.title)}
                          secondary={task.boardName || ts("search.tasks")}
                        />
                      </ListItem>
                    );
                  })}
                  <Divider />
                </>
              )}
              {/* Usuários da plataforma */}
              {users.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ px: 2, pt: 1, color: theme.palette.text.secondary }}>
                    {ts("search.users")}
                  </Typography>
                  {users.map((user, i) => {
                    const thisIndex = suggestionIndexOffsets.users + i;
                    const uid = Number(user?.id);
                    const isOpeningDm = !!openingDmByUser[uid];
                    const avatarUrl = getUserAvatarUrl(user);
                    const displayName = user?.nome || user?.name || "—";
                    const directEmail = String(user?.email || "").trim();
                    const hasResolvedEmail = Object.prototype.hasOwnProperty.call(resolvedEmails, uid);
                    const userEmail =
                      directEmail || (hasResolvedEmail ? String(resolvedEmails[uid] || "").trim() : "");
                    const emailKnown = Boolean(directEmail) || hasResolvedEmail;
                    const canScheduleUser = Boolean(userEmail);
                    const secondary =
                      userEmail ||
                      user?.cargo_nome ||
                      user?.cargo?.nome ||
                      "";

                    return (
                      <ListItem
                        key={`u-${uid}`}
                        onClick={() => handleOpenUserDm(user)}
                        selected={selectedIndex === thisIndex}
                        sx={{
                          cursor: "pointer",
                          backgroundColor:
                            selectedIndex === thisIndex ? theme.palette.action.selected : "transparent",
                          "&:hover": { backgroundColor: theme.palette.action.hover },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            src={avatarUrl || undefined}
                            sx={{
                              backgroundColor: theme.palette.info.main,
                              color: theme.palette.info.contrastText,
                            }}
                          >
                            {!avatarUrl && getInitials(displayName)}
                          </Avatar>
                        </ListItemAvatar>

                        <ListItemText
                          primary={displayName}
                          secondary={secondary}
                          sx={{ flex: "1 1 auto", minWidth: 0, pr: 1 }}
                        />

                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          flexShrink={0}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Tooltip
                            title={
                              emailKnown && !canScheduleUser
                                ? ts("search.userNoEmail")
                                : ts("search.createAppointment")
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                aria-label={ts("search.createAppointment")}
                                disabled={emailKnown && !canScheduleUser}
                                onClick={() =>
                                  handleScheduleWithUser(
                                    userEmail ? { ...user, email: userEmail } : user
                                  )
                                }
                                sx={{
                                  border: `1px solid ${theme.palette.divider}`,
                                  borderRadius: 1,
                                }}
                              >
                                <EventAvailableOutlined fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={ts("search.openInternalChat")}>
                            <span>
                              <IconButton
                                size="small"
                                aria-label={ts("search.openInternalChat")}
                                disabled={isOpeningDm || isMasterUser}
                                onClick={() => handleOpenUserDm(user)}
                                sx={{
                                  border: `1px solid ${theme.palette.divider}`,
                                  borderRadius: 1,
                                }}
                              >
                                {isOpeningDm ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <ChatBubbleOutline fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={ts("search.createTask")}>
                            <span>
                              <IconButton
                                size="small"
                                aria-label={ts("search.createTask")}
                                onClick={() => handleOpenCreateTask(user)}
                                sx={{
                                  border: `1px solid ${theme.palette.divider}`,
                                  borderRadius: 1,
                                }}
                              >
                                <AssignmentOutlined fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </ListItem>
                    );
                  })}
                  <Divider />
                </>
              )}

              {/* Clientes */}
              {clients.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ px: 2, pt: 1, color: theme.palette.text.secondary }}>
                    {ts("search.clients")}
                  </Typography>
                  {clients.map((client, i) => {
                    const thisIndex = suggestionIndexOffsets.clients + i;
                    const isStarting = !!startingByClient[client?.id];

                    const avatarUrl = getClientProfilePictureUrl(
                      client?.imagem || client?.profile_picture
                    );
                    const { count: relationshipsCount, hint: relationshipHint } =
                      getClientListRelationshipProps(client);

                    return (
                      <ListItem
                        key={`cl-${client?.id}`}
                        onClick={() => goToClient(client?.id, client)}
                        selected={selectedIndex === thisIndex}
                        sx={{
                          cursor: "pointer",
                          backgroundColor:
                            selectedIndex === thisIndex ? theme.palette.action.selected : "transparent",
                          "&:hover": { backgroundColor: theme.palette.action.hover },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            src={avatarUrl || undefined}
                            sx={{
                              backgroundColor: theme.palette.primary.main,
                              color: theme.palette.primary.contrastText,
                            }}
                          >
                            {!avatarUrl &&
                              getInitials(getClientCommercialName(client) || client?.nome)}
                          </Avatar>
                        </ListItemAvatar>

                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                              <Typography variant="body1" component="span" noWrap>
                                {getClientCommercialName(client) || client?.nome || "—"}
                              </Typography>
                              {relationshipsCount > 0 ? (
                                <ClientRelationshipBadge
                                  count={relationshipsCount}
                                  hint={relationshipHint}
                                  compact
                                />
                              ) : null}
                            </Box>
                          }
                          secondary={(() => {
                            const razao = getRazaoSocial(client);
                            const commercial = getClientCommercialName(client);
                            const email = client?.email || "";
                            if (
                              isPessoaJuridica(client) &&
                              razao &&
                              razao !== commercial
                            ) {
                              return email ? `${razao} · ${email}` : razao;
                            }
                            return email || "";
                          })()}
                        />

                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isStarting}
                          onClick={(e) => {
                            e.stopPropagation();
                            rememberClientInteraction(client);
                            handleNewConversation(client?.id);
                          }}
                          sx={{
                            minWidth: 140,
                            color: theme.palette.primary.main,
                            borderColor: theme.palette.primary.light,
                            "&:hover": {
                              borderColor: theme.palette.primary.dark,
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        >
                          {isStarting ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <CircularProgress size={16} />
                              {ts("search.starting")}
                            </Box>
                          ) : (
                            ts("search.newRequest")
                          )}
                        </Button>
                      </ListItem>
                    );
                  })}
                  <Divider />
                </>
              )}

              {/* Solicitações */}
              {conversations.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ px: 2, pt: 1, color: theme.palette.text.secondary }}>
                    {ts("search.requests")}
                  </Typography>
                  {conversations.map((conversation, i) => {
                    const thisIndex = suggestionIndexOffsets.conversations + i;

                    const unread = Number(conversation?.unread ?? 0);
                    const name = conversation?.name || "—";
                    const lastMsg = sanitizeTelephonyListPreview(
                      sanitizeText(conversation?.lastMessage || "")
                    );
                    const statusLabel = conversation?.statusLabel || conversation?.status || "";
                    const statusValueForChip =
                      conversation?.status != null && conversation?.status !== ""
                        ? conversation.status
                        : statusLabel;
                    const rawAssigned =
                      conversation?.assignedTo ?? conversation?.userName ?? null;
                    const normalizedAssigned =
                      !rawAssigned ||
                      rawAssigned === "Fila" ||
                      rawAssigned === "Queue"
                        ? null
                        : rawAssigned === "Minha Solicitação" || rawAssigned === "Me"
                        ? userData?.nome || rawAssigned
                        : rawAssigned;

                    return (
                      <ListItem
                        key={`c-${conversation?.id}`}
                        onClick={() => goToConversation(conversation?.id)}
                        selected={selectedIndex === thisIndex}
                        sx={{
                          cursor: "pointer",
                          backgroundColor:
                            selectedIndex === thisIndex ? theme.palette.action.selected : "transparent",
                          "&:hover": { backgroundColor: theme.palette.action.hover },
                        }}
                      >
                        <ListItemAvatar>
                          <Badge color="error" badgeContent={unread} overlap="circular" invisible={!unread}>
                            <Avatar>{name?.[0] || "?"}</Avatar>
                          </Badge>
                        </ListItemAvatar>

                        <Box flexGrow={1} overflow="hidden">
                          <Typography variant="subtitle2" noWrap>{name}</Typography>
                          <Typography variant="body2" color={theme.palette.text.secondary} noWrap>
                            {lastMsg || "—"}
                          </Typography>
                          {renderAssigneeChip(
                            normalizedAssigned,
                            userData,
                            theme,
                            statusValueForChip,
                            ts
                          )}
                        </Box>

                        <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                          {(conversation?.channel ||
                            conversation?.channelLabel ||
                            conversation?.channelType ||
                            conversation?.channel_type) && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {getChannelIcon(
                                conversation?.channel ?? conversation?.channelLabel ?? "",
                                conversation?.channelType ?? conversation?.channel_type ?? ""
                              )}
                              <Typography
                                variant="caption"
                                color={theme.palette.text.secondary}
                                noWrap
                              >
                                {conversation?.channel ??
                                  conversation?.channelLabel ??
                                  conversation?.channelType ??
                                  conversation?.channel_type}
                              </Typography>
                            </Box>
                          )}
                          <Typography variant="caption" color={theme.palette.text.primary}>
                            {ts("search.protocol", { id: conversation?.id })}
                          </Typography>
                          <Typography variant="caption" color="#aaa">
                            {safeTimestamp(conversation)}
                          </Typography>
                          {getStatusChip(statusValueForChip, theme)}
                        </Stack>
                      </ListItem>
                    );
                  })}
                  <Divider />
                </>
              )}

              {/* Histórico */}
              {history.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ px: 2, pt: 1, color: theme.palette.text.secondary }}>
                    {ts("search.searchHistory")}
                  </Typography>

                  {history.map((historyItem, index) => {
                    const thisIndex = suggestionIndexOffsets.history + index;
                    return (
                      <ListItem
                        key={`h-${index}`}
                        onClick={() => {
                          setQuery(historyItem);
                          goToSearchResults(`/search-results?q=${encodeURIComponent(historyItem)}`);
                        }}
                        selected={selectedIndex === thisIndex}
                        sx={{
                          cursor: "pointer",
                          backgroundColor:
                            selectedIndex === thisIndex ? theme.palette.action.selected : "transparent",
                          "&:hover": { backgroundColor: theme.palette.action.hover },
                        }}
                      >
                        <ListItemAvatar>
                          <IconButton size="small">
                            <History sx={{ color: theme.palette.text.secondary }} />
                          </IconButton>
                        </ListItemAvatar>
                        <ListItemText primary={historyItem} />
                      </ListItem>
                    );
                  })}
                  <Divider />
                </>
              )}

              {/* Vendas (Pipeline) */}
              {sales.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ px: 2, pt: 1, color: theme.palette.text.secondary }}>
                    {ts("search.pipelineSales")}
                  </Typography>
                  {sales.map((sale, i) => {
                    const thisIndex = suggestionIndexOffsets.sales + i;

                    const total = formatCurrencyBRL(sale?.total);
                    const itemsLabel =
                      sale?.items && sale.items.length > 0
                        ? `${sale.items[0].name || ts("search.productFallback")}${
                            sale.items.length > 1 ? ` +${sale.items.length - 1}` : ""
                          }`
                        : "—";

                    const typePath = buildSaleTypePath(sale);
                    const statusLabel = sale?.statusLabel || "";
                    const statusValueForChip = sale?.status ?? sale?.statusCode ?? statusLabel;

                    return (
                      <ListItem
                        key={`s-${sale.id}`}
                        onClick={() => goToSale(sale.id)}
                        selected={selectedIndex === thisIndex}
                        sx={{
                          cursor: "pointer",
                          backgroundColor:
                            selectedIndex === thisIndex ? theme.palette.action.selected : "transparent",
                          "&:hover": { backgroundColor: theme.palette.action.hover },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              backgroundColor: theme.palette.secondary.main,
                              color: theme.palette.secondary.contrastText,
                            }}
                          >
                            {(sale?.companyName || "V")?.[0]}
                          </Avatar>
                        </ListItemAvatar>

                        <Box flexGrow={1} overflow="hidden">
                          <Typography variant="subtitle2" noWrap>
                            {sale?.companyName || ts("search.clientFallback")}
                          </Typography>
                          <Typography variant="body2" color={theme.palette.text.secondary} noWrap>
                            {itemsLabel}
                          </Typography>
                          {typePath && (
                            <Typography variant="caption" color={theme.palette.text.secondary} noWrap>
                              {typePath}
                            </Typography>
                          )}
                          <Typography variant="body2" color={theme.palette.text.primary}>
                            {total}
                          </Typography>
                        </Box>

                        <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                          <Typography variant="caption" color={theme.palette.text.secondary}>
                            {sale?.orderCode || `COD-${sale.id}`}
                          </Typography>
                          <Typography variant="caption" color="#aaa">
                            {safeSalesDate(sale)}
                          </Typography>
                          {(sale?.channelType || sale?.channelLabel) && (
                            <Box sx={{ mt: 0.25, display: "flex", alignItems: "center", gap: 0.5 }}>
                              {getChannelIcon(
                                sale?.channelLabel || sale?.channelType || "",
                                sale?.channelType || ""
                              )}
                              <Typography variant="caption" color={theme.palette.text.secondary} noWrap>
                                {sale?.channelLabel || sale?.channelType}
                              </Typography>
                            </Box>
                          )}
                          {statusValueForChip && getStatusChip(statusValueForChip, theme)}
                        </Stack>
                      </ListItem>
                    );
                  })}
                  <Divider />
                </>
              )}

              {/* Sugestões de ajuda / dúvidas */}
              {helpResults.length > 0 && (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{ px: 2, pt: 1, color: theme.palette.primary.main, fontWeight: 600 }}
                  >
                    {ts("search.quickAnswers")}
                  </Typography>
                  {helpResults.map((topic, index) => {
                    const thisIndex = suggestionIndexOffsets.help + index;

                    return (
                      <ListItem
                        key={`hhelp-${topic.id}`}
                        onClick={() => {
                          goToHelpTopic(topic.route);
                        }}
                        selected={selectedIndex === thisIndex}
                        sx={{
                          cursor: "pointer",
                          alignItems: "flex-start",
                          backgroundColor:
                            selectedIndex === thisIndex
                              ? theme.palette.action.selected
                              : "transparent",
                          "&:hover": { backgroundColor: theme.palette.action.hover },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight={600}>
                              {topic.title}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {topic.summary}
                            </Typography>
                          }
                        />
                      </ListItem>
                    );
                  })}
                  <Divider />
                </>
              )}

              {taskHits.length === 0 &&
                history.length === 0 &&
                conversations.length === 0 &&
                clients.length === 0 &&
                users.length === 0 &&
                sales.length === 0 &&
                helpResults.length === 0 && (
                  <Typography textAlign="center" sx={{ p: 2, color: theme.palette.text.disabled }}>
                    {ts("search.noResults")}
                  </Typography>
                )}
            </List>
          )}
        </Box>
      )}

      <AgendaEventDialog
        open={agendaDialogOpen}
        onClose={() => {
          if (createAgendaMutation.isLoading) return;
          setAgendaDialogOpen(false);
          setAgendaDefaultRange(null);
          setAgendaDialogError("");
        }}
        initialEvent={null}
        defaultRange={agendaDefaultRange}
        onSave={handleAgendaSave}
        saving={createAgendaMutation.isLoading}
        error={agendaDialogError}
        calendars={agendaCalendars}
        defaultCalendarId={defaultAgendaCalendarId}
      />

      <CreateLinkedTaskDialog
        open={taskDialogOpen}
        onClose={() => {
          if (creatingTask) return;
          setTaskDialogOpen(false);
          setTaskDialogUser(null);
        }}
        initialAssignees={
          taskDialogUser
            ? [
                {
                  id: Number(taskDialogUser.id),
                  nome: taskDialogUser.nome || taskDialogUser.name || "",
                },
              ]
            : []
        }
        onSubmit={handleCreateTaskSubmit}
        isSubmitting={creatingTask}
        dialogTitle={ts("search.createTaskTitle")}
        dialogIntro={ts("search.createTaskIntro")}
        submitLabel={ts("search.createTask")}
        boardHelperText={ts("search.createTaskBoardHint")}
        assigneesHelperText={ts("search.createTaskAssigneesHint")}
      />

      {/* Snackbar de erro ao iniciar solicitação */}
      <Snackbar
        open={!!startError}
        autoHideDuration={6000}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setStartError(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setStartError(null)} severity="error" variant="filled" sx={{ width: "100%" }}>
          {startError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SearchAutocomplete;